import React from 'react';

import { Box, Typography } from '@mui/material';

import { balanceFormatter, numberWithComma } from '~/utils/helperFunctions';
import { dateFormat } from '~/utils/date-format';
import {
  getFindNewContactsConfig,
  getFindNewContactsSearchMethod,
} from '../../utils/find-new-contacts';
import {
  getActiveFilterRows,
  getDataAxleFilterByKey,
} from '../../../CreateOrderNew/FindNewContacts/utils/filters';
import {
  ContactCIcon,
  InActiveFilterCIcon,
  GeneralLocationCIcon,
  CheckedCIcon,
  OrderDetailsCIcon,
  TotalContactsCIcon,
  TotalCostCIcon,
} from '~/assets/images';

import './styles.scss';

const titleize = (value = '') =>
  `${value}`
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const supportedHomeFeatureValues = new Set(['fireplace', 'garage', 'pool']);
const removedSavedFilterLabelPrefixes = new Set([
  'foundation type:',
  'roof cover:',
  'roof type:',
]);

const hasValue = (value) => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.values(value).some(hasValue);
  return value !== null && value !== undefined && value !== '';
};

const sanitizeSavedFilterLabel = (label) => {
  const normalizedLabel = `${label || ''}`.trim();
  if (removedSavedFilterLabelPrefixes.has(normalizedLabel.split(':')[0].toLowerCase() + ':')) {
    return '';
  }

  const homeFeaturesPrefix = 'Home features:';
  if (!normalizedLabel.toLowerCase().startsWith(homeFeaturesPrefix.toLowerCase())) {
    return normalizedLabel;
  }

  const values = normalizedLabel
    .slice(homeFeaturesPrefix.length)
    .split(',')
    .map((value) => value.trim())
    .filter((value) => value && supportedHomeFeatureValues.has(value.toLowerCase()));

  return values.length ? `${homeFeaturesPrefix} ${values.join(', ')}` : '';
};

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(`${value}`.replace(/[$,]/g, ''));
  return Number.isFinite(parsed) ? parsed : null;
};

const firstNumber = (...values) => {
  for (const value of values) {
    const parsed = toNumber(value);
    if (parsed !== null) return parsed;
  }
  return null;
};

const formatCostPerContact = (value) => {
  const parsed = toNumber(value);
  if (parsed === null) return '-';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(parsed);
};

const getFilterLabels = (config = {}) => {
  const labels = [
    ...(Array.isArray(config.filterLabels) ? config.filterLabels : []),
    ...(Array.isArray(config.addressConfigs)
      ? config.addressConfigs.flatMap((addressConfig) =>
        Array.isArray(addressConfig.filterLabels) ? addressConfig.filterLabels : []
      )
      : []),
  ]
    .map((label) => (typeof label === 'string' ? label : label?.label || label?.value))
    .map(sanitizeSavedFilterLabel)
    .filter(Boolean);

  return [...new Set(labels)];
};

const getSavedFilterRows = (config = {}) =>
  getFilterLabels(config).map((filterLabel) => {
    const separatorIndex = filterLabel.indexOf(':');
    if (separatorIndex < 0) return { label: filterLabel, value: 'Applied' };

    return {
      label: filterLabel.slice(0, separatorIndex).trim(),
      value: filterLabel.slice(separatorIndex + 1).trim() || 'Applied',
    };
  });

const getAppliedFilterRows = (config = {}) => {
  const savedFilterRows = getSavedFilterRows(config);
  const savedFilterRowByLabel = new Map(
    savedFilterRows.map((row) => [row.label.toLowerCase(), row])
  );
  const filterByKey = getDataAxleFilterByKey(config.contactType);
  const filterRows = getActiveFilterRows(config.filters || {}, {
    contactType: config.contactType,
    showAllOptions: true,
  }).map((row) => {
    const savedRow = savedFilterRowByLabel.get(row.label.toLowerCase());

    return filterByKey[row.key]?.asyncLookup && savedRow
      ? { ...row, value: savedRow.value }
      : row;
  });

  return filterRows.length ? filterRows : savedFilterRows;
};

const getAddressLabel = (address) => {
  if (!address) return null;
  if (typeof address === 'string') return address;
  return address.formattedAddress || address.address || address.label || null;
};

const targetCopyByContactType = {
  businesses: {
    plural: 'businesses',
    targetLabel: 'Number of Business',
    targetLabelAroundAddress: 'Number of Businesses around an address',
    targetSuffix: 'around an address',
  },
  consumers: {
    plural: 'homes',
    targetLabel: 'Number of homes',
    targetLabelAroundAddress: 'Number of homes',
    targetSuffix: 'around address',
  },
};

const getTargetCopy = (contactType) =>
  targetCopyByContactType[contactType] || targetCopyByContactType.consumers;

const formatRecordTargetValue = (count, contactType) => {
  const targetCopy = getTargetCopy(contactType);

  return hasValue(count)
    ? `${numberWithComma(count)} ${targetCopy.plural} ${targetCopy.targetSuffix}`
    : null;
};

const formatRadiusTargetValue = (radiusMiles) =>
  (hasValue(radiusMiles) ? `${radiusMiles} miles radius` : null);

const getAreaTargetValue = ({ contactType, count, mode, radiusMiles }) =>
  (mode === 'homes'
    ? formatRecordTargetValue(count, contactType)
    : formatRadiusTargetValue(radiusMiles));

const getAreaRows = (config = {}) => {
  const rows = [];
  const addresses = Array.isArray(config.addresses) ? config.addresses : [];
  const addressConfigs = Array.isArray(config.addressConfigs) ? config.addressConfigs : [];

  if (addressConfigs.length) {
    addressConfigs.forEach((addressConfig, index) => {
      const addressLabel = getAddressLabel(addresses[index]) || addressConfig.address || `Address ${index + 1}`;
      const mode = addressConfig.nearbyTargetMode || config.nearbyTargetMode;
      const radius = addressConfig.radiusMiles || config.radiusMiles;
      const count = addressConfig.targetCount || config.targetCount;
      rows.push({
        label: addressLabel,
        value: getAreaTargetValue({
          contactType: addressConfig.contactType || config.contactType,
          count,
          mode,
          radiusMiles: radius,
        }),
      });
    });
  } else if (addresses.length) {
    addresses.forEach((address, index) => {
      rows.push({
        label: getAddressLabel(address) || `Address ${index + 1}`,
        value: getAreaTargetValue({
          contactType: config.contactType,
          count: config.targetCount,
          mode: config.nearbyTargetMode,
          radiusMiles: config.radiusMiles,
        }),
      });
    });
  } else if (config.address) {
    rows.push({
      label: config.address,
      value: getAreaTargetValue({
        contactType: config.contactType,
        count: config.targetCount,
        mode: config.nearbyTargetMode,
        radiusMiles: config.radiusMiles,
      }),
    });
  }

  if (Array.isArray(config.drawRadiusAreas)) {
    config.drawRadiusAreas.forEach((area, index) => {
      rows.push({
        label: area.label || area.address || `Radius area ${index + 1}`,
        value: formatRadiusTargetValue(area.radiusMiles),
      });
    });
  }

  if (Array.isArray(config.customShapes)) {
    config.customShapes.forEach((shape, index) => {
      const points = Array.isArray(shape) ? shape.length : 0;
      rows.push({
        label: `Drawn area ${index + 1}`,
        value: points ? `${points} boundary points` : 'Custom boundary',
      });
    });
  }

  return rows;
};

const getTargetingLabel = (config = {}, searchMethodLabel = '') => {
  if (['custom', 'draw-radius', 'manual'].includes(config.areaMode)) return 'Custom area';
  if (config.nearbyTargetMode === 'homes') {
    const targetCopy = getTargetCopy(config.contactType);

    return searchMethodLabel === 'Radius Around an Address'
      ? targetCopy.targetLabelAroundAddress
      : targetCopy.targetLabel;
  }
  if (config.nearbyTargetMode === 'radius') return 'Radius';
  return titleize(config.areaMode || 'Selected area');
};

const SectionHeader = ({ icon, title }) => (
  <Box className="fncSectionHeader">
    <img src={icon} alt="" />
    <Typography className="fncSectionTitle">{title}</Typography>
  </Box>
);

function FindNewContactsSummary({ data, overview }) {
  const contactImportLog = overview?.contactImportLog || data?.contactImportLogs?.[0] || {};
  const meta = contactImportLog.meta || data?.meta || {};
  const config = getFindNewContactsConfig(data, overview);
  const purchase = meta.purchase || data?.meta?.purchase || {};
  const targetCount = purchase.quantity || meta.targetCount || contactImportLog.quantity || data?.quantity;
  const importedContacts = meta.actualContacts || meta.imported || overview?.totalContacts || 0;
  const totalContacts = targetCount || importedContacts || 0;
  const totalContactsNumber = toNumber(totalContacts);
  const totalCost = firstNumber(overview?.cost, data?.cost, purchase.totalCost, purchase.cost);
  const explicitCostPerContact = firstNumber(
    purchase.costPerContact,
    purchase.pricePerContact,
    purchase.perContactCost,
    purchase.unitPrice,
    purchase.unitCost,
    purchase.price,
    meta.costPerContact,
    meta.contactUnitPrice,
    meta.unitPrice,
    config.contactUnitPrice
  );
  const costPerContact =
    explicitCostPerContact !== null
      ? explicitCostPerContact
      : totalCost && totalContactsNumber
        ? totalCost / totalContactsNumber
        : null;
  const orderStatus = data?.status || meta.importStatus || contactImportLog.status || '-';
  const filterRows = getAppliedFilterRows(config);
  const areaRows = getAreaRows(config);
  const searchMethodLabel = getFindNewContactsSearchMethod(data, overview);
  const summaryCards = [
    {
      icon: TotalCostCIcon,
      label: 'Total Cost',
      value: balanceFormatter(totalCost, 3),
      primary: true,
    },
    {
      icon: TotalContactsCIcon,
      label: 'Total Contacts',
      value: numberWithComma(totalContacts),
    },
    {
      icon: ContactCIcon,
      label: 'Cost Per Contact',
      value: formatCostPerContact(costPerContact),
    },
    {
      icon: CheckedCIcon,
      label: 'Order Status',
      value: titleize(orderStatus),
    },
  ];
  const detailRows = [
    { label: 'Order Name', value: data?.name },
    { label: 'Search Method', value: searchMethodLabel },
    { label: 'Targeting', value: getTargetingLabel(config, searchMethodLabel) },
    { label: 'Target Areas', value: areaRows.length || '-' },
    { label: 'Purchased On', value: purchase.purchasedAt ? dateFormat(purchase.purchasedAt, true) : null },
    { label: 'Payment Status', value: data?.paymentStatus || '-' },
  ].filter((row) => row.value !== null && row.value !== undefined && row.value !== '');

  return (
    <Box className="findNewContactsSummary">
      <Box className="total_cost fncSummaryCards">
        {summaryCards.map((card) => (
          <Box key={card.label} className={card.primary ? 'total_card' : 'card'}>
            <img width={40} height={40} src={card.icon} alt="" />
            <Box className="meta_info_box">
              <span className="meta_info">{card.label}</span>
              <Typography className={card.primary ? 'ammount' : 'cost'}>{card.value}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box className="fncSection">
        <SectionHeader icon={OrderDetailsCIcon} title="Order Details" />
        <Box className="fncDetailGrid">
          {detailRows.map((row) => (
            <Box key={row.label} className="fncDetailItem">
              <Typography className="fncDetailLabel">{row.label}</Typography>
              <Typography className="fncDetailValue">{row.value}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box className="fncSection">
        <SectionHeader icon={GeneralLocationCIcon} title="Target Areas" />
        <Box className="fncRows">
          {areaRows.length ? areaRows.map((row, index) => (
            <Box key={`${row.label}-${index}`} className="fncRow">
              <Typography className="fncRowLabel">{row.label}</Typography>
              {hasValue(row.value) && (
                <Typography className="fncRowValue">{row.value}</Typography>
              )}
            </Box>
          )) : (
            <Typography className="fncEmpty">No saved target areas.</Typography>
          )}
        </Box>
      </Box>

      <Box className="fncSection">
        <SectionHeader icon={InActiveFilterCIcon} title="Filters" />
        {filterRows.length ? (
          <Box className="fncRows">
            {filterRows.map((row) => (
              <Box key={row.label} className="fncRow">
                <Typography className="fncRowLabel">{row.label}</Typography>
                <Typography className="fncRowValue">{row.value}</Typography>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography className="fncEmpty">No filters were applied.</Typography>
        )}
      </Box>
    </Box>
  );
}

export default FindNewContactsSummary;
